import { Button, Flex, IconButton, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import {
  ChevronUpOutline,
  CupOutline,
  DropdownOption,
  GridFiveOutline,
  PageEmptyState,
  RecognitionCard,
  TaskOutline,
} from '@aslanpay/portal-common-lib';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import { useManagingCompanyStore } from 'app/store';
import useGroups from 'app/hooks/useGroups';
import useFetchRecognitionValues from 'app/hooks/useFetchRecognitionValues';
import { useEffect, useMemo, useRef, useState } from 'react';

import TheRoarFilters from './TheRoarFilters/TheRoarFilters';
import useTheRoar from './useTheRoar';

function TheRoar() {
  const company = useManagingCompanyStore((state) => state.company);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = pageRef.current?.closest('[data-scroll-container]') ?? window;
    const getScrollTop = () =>
      container === window ? window.scrollY : (container as Element).scrollTop;

    const handleScroll = () => setShowScrollTop(getScrollTop() > 300);
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    const container = pageRef.current?.closest('[data-scroll-container]') ?? window;
    container.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const companyId = company?.id ?? '';

  const { data: groupTree } = useGroups.useFetchGroupsTree(
    companyId || undefined,
    undefined,
    !!companyId
  );

  const { data: valuesData } = useFetchRecognitionValues(
    { companyId, isEnabled: true },
    !!companyId
  );

  const valueOptions: DropdownOption[] = useMemo(
    () => (valuesData?.list ?? []).map((v) => ({ value: v.id, text: `${v.emoji} ${v.name}` })),
    [valuesData]
  );

  const {
    viewMode,
    setViewMode,
    selectedGroupIds,
    setSelectedGroupIds,
    selectedValueIds,
    setSelectedValueIds,
    timeOption,
    customFrom,
    customTo,
    searchQuery,
    setSearchQuery,
    handleTimeOptionChange,
    setCustomFrom,
    setCustomTo,
    recognitions,
    infiniteQuery,
    handleHide,
    handleUnhide,
    pendingHideId,
  } = useTheRoar(companyId || undefined);

  const { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = infiniteQuery;

  return (
    <AuthenticatedPageWrapper title="The Roar">
      <Flex flexDir="column" gap="24px" ref={pageRef}>
        <Flex justify="space-between" align="flex-start">
          <Flex flexDir="column" gap="4px">
            <Text as="h2" textStyle="h2" color="brand.darkPurple">
              The Roar
            </Text>
            <Text color="brand.darkPurple">Monitor your teams daily shout-outs below</Text>
          </Flex>

          <Flex
            border="1px solid"
            borderColor="brand.lightGray1"
            borderRadius="8px"
            overflow="hidden"
          >
            <Button
              variant="ghost"
              size="sm"
              px="16px"
              bg={viewMode === 'list' ? 'white' : 'brand.lightGray1'}
              color={viewMode === 'list' ? 'brand.purple' : 'brand.gray'}
              borderRadius={0}
              boxShadow={viewMode === 'list' ? 'md' : 'none'}
              pointerEvents={viewMode === 'list' ? 'none' : 'auto'}
              onClick={() => setViewMode('list')}
            >
              <TaskOutline boxSize="16px" mr="6px" />
              List
            </Button>
            <Button
              variant="ghost"
              size="sm"
              px="16px"
              bg={viewMode === 'grid' ? 'white' : 'brand.lightGray1'}
              color={viewMode === 'grid' ? 'brand.purple' : 'brand.gray'}
              borderRadius={0}
              boxShadow={viewMode === 'grid' ? 'md' : 'none'}
              pointerEvents={viewMode === 'grid' ? 'none' : 'auto'}
              onClick={() => setViewMode('grid')}
            >
              <GridFiveOutline boxSize="16px" mr="6px" />
              Grid
            </Button>
          </Flex>
        </Flex>

        <TheRoarFilters
          groupTree={groupTree ?? []}
          valueOptions={valueOptions}
          selectedGroupIds={selectedGroupIds}
          selectedValueIds={selectedValueIds}
          timeOption={timeOption}
          customFrom={customFrom}
          customTo={customTo}
          searchQuery={searchQuery}
          onGroupsChange={setSelectedGroupIds}
          onValuesChange={setSelectedValueIds}
          onTimeOptionChange={handleTimeOptionChange}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onSearchChange={setSearchQuery}
        />

        {isLoading && <CenterSpinner />}

        {!isLoading && recognitions.length === 0 && (
          <PageEmptyState
            icon={<CupOutline color="brand.purple" boxSize="40px" />}
            title="No recognitions found"
            description="No approved recognitions match your current filters."
          />
        )}

        {!isLoading && recognitions.length > 0 && viewMode === 'list' && (
          <Flex
            data-testid="recognitions-list"
            flexDir="column"
            gap="16px"
            maxW="547px"
            mx="auto"
            w="full"
          >
            {recognitions.map((recognition) => (
              <RecognitionCard
                key={recognition.id}
                recognition={recognition}
                variant="list"
                canModerate
                onHide={handleHide}
                onUnhide={handleUnhide}
                isHidePending={pendingHideId === recognition.id}
              />
            ))}
          </Flex>
        )}

        {!isLoading && recognitions.length > 0 && viewMode === 'grid' && (
          <SimpleGrid
            data-testid="recognitions-grid"
            gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))"
            gap="16px"
          >
            {recognitions.map((recognition) => (
              <RecognitionCard
                key={recognition.id}
                recognition={recognition}
                variant="grid"
                canModerate
                onHide={handleHide}
                onUnhide={handleUnhide}
                isHidePending={pendingHideId === recognition.id}
              />
            ))}
          </SimpleGrid>
        )}

        {hasNextPage && (
          <Flex justify="center" mt="8px">
            <Button
              variant="ghost"
              onClick={() => fetchNextPage()}
              isLoading={isFetchingNextPage}
              loadingText="Loading..."
              rightIcon={<ChevronUpOutline boxSize="16px" transform="rotate(180deg)" />}
              _hover={{ bg: 'transparent' }}
            >
              Load more
            </Button>
          </Flex>
        )}

        {isFetchingNextPage && !hasNextPage && (
          <Flex justify="center">
            <Spinner size="sm" color="brand.purple" />
          </Flex>
        )}
      </Flex>

      {showScrollTop && (
        <IconButton
          aria-label="Scroll to top"
          icon={<ChevronUpOutline boxSize="24px" />}
          position="fixed"
          bottom="40px"
          right="40px"
          zIndex={10}
          borderRadius="full"
          bg="brand.purple"
          color="white"
          boxSize="48px"
          _hover={{ opacity: 0.85 }}
          onClick={scrollToTop}
        />
      )}
    </AuthenticatedPageWrapper>
  );
}

export default TheRoar;
